import { baseApi } from "../api";
import { RegisterUserType } from "../../pages/Auth";
import {
  AccountStats,
  LoginPayload,
  ResetPasswordPayload,
  RevokeSessionsResponse,
  SsoLoginResponse,
  UpdateUserDetails,
  UpdateUserPassword,
  UserDetails,
  UserProfile,
} from "./types";

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query<UserProfile, void>({
      query: () => ({ url: "/auth/me", unwrapResult: false }),
      providesTags: ["User"],
    }),

    getUserDetails: builder.query<UserDetails, void>({
      query: () => ({ url: "/auth/details" }),
      providesTags: ["User"],
    }),

    getAccountStats: builder.query<AccountStats, void>({
      query: () => ({ url: "/auth/account-stats" }),
      providesTags: ["AccountStats"],
    }),

    updateUserDetails: builder.mutation<void, UpdateUserDetails>({
      query: (payload) => ({
        url: "/auth/details",
        method: "patch",
        data: payload,
      }),
      invalidatesTags: ["User"],
    }),

    /**
     * A password change can revoke the user's sibling sessions server-side, so
     * the session count in `AccountStats` goes stale. Refetching it costs one
     * request and keeps "Active sessions" honest.
     */
    updateUserPassword: builder.mutation<void, UpdateUserPassword>({
      query: (payload) => ({
        url: "/auth/password",
        method: "put",
        data: payload,
      }),
      invalidatesTags: ["AccountStats"],
    }),

    revokeAllSessions: builder.mutation<RevokeSessionsResponse, void>({
      query: () => ({
        url: "/auth/sessions",
        method: "delete",
        unwrapResult: false,
      }),
      invalidatesTags: ["AccountStats"],
    }),

    deactivateUser: builder.mutation<void, void>({
      query: () => ({ url: "/auth/deactivate", method: "post" }),
    }),

    deleteUser: builder.mutation<void, void>({
      query: () => ({ url: "/auth", method: "delete" }),
    }),

    login: builder.mutation<void, LoginPayload>({
      query: (payload) => ({
        url: "/auth/login",
        method: "post",
        data: payload,
      }),
    }),

    register: builder.mutation<void, RegisterUserType>({
      query: (payload) => ({
        url: "/auth/register",
        method: "post",
        data: payload,
      }),
    }),

    forgotPassword: builder.mutation<string, string>({
      query: (email) => ({
        url: "/auth/forgot",
        method: "post",
        data: { email },
      }),
    }),

    resetPassword: builder.mutation<string, ResetPasswordPayload>({
      query: (payload) => ({
        url: "/auth/reset",
        method: "post",
        data: payload,
      }),
    }),

    logout: builder.mutation<void, void>({
      query: () => ({ url: "/auth/logout" }),
    }),

    ssoLogin: builder.mutation<string, void>({
      query: () => ({ url: "/auth/google", unwrapResult: false }),
      transformResponse: (response: SsoLoginResponse) => response.url,
    }),
  }),
});

export const {
  useGetProfileQuery,
  useGetUserDetailsQuery,
  useGetAccountStatsQuery,
  useUpdateUserDetailsMutation,
  useUpdateUserPasswordMutation,
  useRevokeAllSessionsMutation,
  useDeactivateUserMutation,
  useDeleteUserMutation,
  useLoginMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useLogoutMutation,
  useSsoLoginMutation,
} = authApi;
